import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../utils/analytics';
import AiCompanion from '../components/AiCompanion';
import EmailSubscribeBox from '../components/EmailSubscribeBox';
import './PublicChannel.css';

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const seconds = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function PublicChannelFeed() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [channel, setChannel] = useState(null);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Detect if logged-in user is viewing in read-only mode (non-matching identity)
  const isReadonly = new URLSearchParams(window.location.search).get('readonly') === '1';
  const isLoggedInReadonly = user && isReadonly;
  const [openComments, setOpenComments] = useState({});
  const [comments, setComments] = useState({});

  // After login/register, bring user back to this channel
  const returnTo = encodeURIComponent(`/b/${slug}`);
  const loginUrl = `/login?returnTo=${returnTo}`;
  const registerUrl = `/register?returnTo=${returnTo}`;

  useEffect(() => {
    fetchChannel(1);
    trackEvent('public_channel_view', { channel: slug });
  }, [slug]);

  // If user is logged in and we know the channel ID, redirect to the real channel
  useEffect(() => {
    if (channel) {
      document.title = `b/${slug} — Anonymous ${channel.category_display} Discussion | Belong`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        const snippet = posts.length > 0
          ? posts[0].content.substring(0, 120).replace(/\n/g, ' ') + '...'
          : `Anonymous discussions about ${channel.name}`;
        meta.setAttribute('content',
          `🔒 Anonymous by default | ${snippet} — Join ${typeof channel.member_count === 'string' ? channel.member_count : channel.member_count + ' people'} talking anonymously on Belong. Identity shared only through mutual consent.`
        );
      }
    }
  }, [channel, posts, slug]);

  const fetchChannel = async (pageNum) => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/public/channels/${slug}?page=${pageNum}`);
      setChannel(res.data.channel);
      if (pageNum === 1) {
        setPosts(res.data.posts);
      } else {
        setPosts(prev => [...prev, ...res.data.posts]);
      }
      setHasMore(res.data.has_more);
      setPage(pageNum);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Community not found');
      } else {
        setError('Failed to load community');
      }
    } finally {
      setLoading(false);
    }
  };

  // If user is logged in and we know the channel ID, redirect to the real channel
  // BUT only if they weren't redirected here due to access denial (check URL param)
  useEffect(() => {
    if (user && channel && channel.channel_id) {
      const params = new URLSearchParams(window.location.search);
      // Don't redirect if user was sent here because they lack access
      if (params.get('readonly') === '1') return;
      navigate(`/channels/${channel.channel_id}`, { replace: true });
    }
  }, [user, channel, navigate]);

  const toggleComments = async (postId) => {
    if (openComments[postId]) {
      setOpenComments(prev => ({ ...prev, [postId]: false }));
      return;
    }
    // Fetch comments if not already loaded
    if (!comments[postId]) {
      try {
        const res = await axios.get(`/api/public/channels/${slug}/comments/${postId}`);
        setComments(prev => ({ ...prev, [postId]: res.data || [] }));
      } catch (err) {
        setComments(prev => ({ ...prev, [postId]: [] }));
      }
    }
    setOpenComments(prev => ({ ...prev, [postId]: true }));
  };

  if (error) {
    return (
      <div className="public-channel-page">
        <div className="public-channel-top-bar">
          <Link to="/b" className="public-channel-logo">
            <img src="/belong.png" alt="Belong" />
            <span>Belong</span>
          </Link>
          <div className="public-channel-auth-links">
            <Link to={loginUrl} className="public-auth-link">Sign In</Link>
            <Link to={registerUrl} className="public-auth-btn">Join Belong</Link>
          </div>
        </div>
        <div className="public-channel-container" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h2>😕 {error}</h2>
          <p><Link to="/b">Browse all communities</Link></p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-channel-page">
      {isLoggedInReadonly ? (
        <div className="public-channel-top-bar">
          <Link to="/dashboard" className="public-channel-logo">
            <img src="/belong.png" alt="Belong" />
            <span>Belong</span>
          </Link>
          <div className="public-channel-auth-links">
            <Link to="/dashboard" className="public-auth-btn">← Back to Dashboard</Link>
          </div>
        </div>
      ) : (
        <div className="public-channel-top-bar">
          <Link to="/b" className="public-channel-logo">
            <img src="/belong.png" alt="Belong" />
            <span>Belong</span>
          </Link>
          <div className="public-channel-auth-links">
            <Link to={loginUrl} className="public-auth-link">Sign In</Link>
            <Link to={registerUrl} className="public-auth-btn">Join Belong</Link>
          </div>
        </div>
      )}

      <div className="public-anon-banner">
        <span className="anon-icon">🛡️</span>
        <span>Everything here is <strong>anonymous by default</strong>. Identity is only shared through mutual consent — both sides agree, one level at a time.</span>
      </div>

      {channel && (
        <div className="public-channel-container">
          <div className="public-feed-header">
            <Link to="/b" className="public-feed-back">← All Communities</Link>
            <h1>{channel.category_icon || '💬'} b/{slug}</h1>
            <div className="public-feed-header-meta">
              {channel.name} · {typeof channel.member_count === 'string' ? channel.member_count : `${channel.member_count} members`} · {channel.total_posts} posts
            </div>
          </div>

          {isLoggedInReadonly ? (
            <div className="public-feed-join-prompt" style={{ background: '#fef3c7', borderColor: '#fde68a' }}>
              <span>👀</span>
              <span>You're browsing this community in read-only mode. To post and connect here, <Link to="/profile">add this identity to your profile</Link>.</span>
            </div>
          ) : (
            <div className="public-feed-join-prompt">
              <span>🔒</span>
              <span>Want to share your experience? <Link to={registerUrl}>Join anonymously</Link> — your identity stays private unless you choose to share through mutual consent.</span>
            </div>
          )}

          {isLoggedInReadonly ? (
            <div className="public-composer-locked" style={{ background: '#fefce8', borderColor: '#fde68a' }}>
              <div className="public-composer-locked-icon">👀</div>
              <div className="public-composer-locked-text">
                You can read posts here, but to participate, <Link to="/profile">add this identity</Link> to your profile
              </div>
            </div>
          ) : (
            <div className="public-composer-locked">
              <div className="public-composer-locked-icon">🔒</div>
              <div className="public-composer-locked-text">
                <Link to={registerUrl}>Sign up</Link> or <Link to={loginUrl}>log in</Link> to post and reply — you stay anonymous unless both sides agree to share
              </div>
            </div>
          )}

          {loading && posts.length === 0 ? (
            <div className="public-feed-loading">Loading discussions...</div>
          ) : posts.length === 0 ? (
            <div className="public-feed-empty">
              <p>No discussions yet in this community.</p>
              <Link to={registerUrl}>Be the first to start a conversation →</Link>
            </div>
          ) : (
            <div className="public-feed-posts">
              {posts.map((post, index) => (
                <React.Fragment key={post.post_id}>
                  {/* Inline subscribe prompt after 3rd post */}
                  {index === 3 && !user && (
                    <div style={{ padding: '16px 20px', background: '#f5f3ff', borderRadius: '12px', border: '1px solid #ede9fe', textAlign: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#4b5563' }}>📬 Enjoying these? </span>
                      <span style={{ fontSize: '14px', color: '#7C3AED', fontWeight: 600 }}>Get one post like this in your inbox each week.</span>
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        const emailInput = e.target.elements.inlineEmail;
                        if (!emailInput.value.trim()) return;
                        try {
                          await axios.post('/api/public/subscribe', { email: emailInput.value.trim(), channelSlug: slug, categoryName: channel?.category_display });
                          emailInput.value = '';
                          e.target.querySelector('.inline-sub-msg').textContent = '✓ Subscribed!';
                        } catch (err) {
                          e.target.querySelector('.inline-sub-msg').textContent = err.response?.status === 409 ? 'Already subscribed!' : 'Try again later.';
                        }
                      }} style={{ display: 'flex', gap: '8px', marginTop: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <input name="inlineEmail" type="email" placeholder="your@email.com" required style={{ padding: '8px 14px', border: '1px solid #e5e7eb', borderRadius: '20px', fontSize: '13px', width: '200px' }} />
                        <button type="submit" style={{ padding: '8px 16px', background: '#7C3AED', color: 'white', border: 'none', borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Subscribe</button>
                      </form>
                      <p className="inline-sub-msg" style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>No spam. Unsubscribe anytime.</p>
                    </div>
                  )}
                  <div className="public-post-card">
                  <div className="public-post-author">
                    <span className="public-post-avatar">{post.is_ai_generated ? '🌱' : '👤'}</span>
                    <span className="public-post-name">{post.is_ai_generated ? 'Belong' : 'Anonymous'}</span>
                    {post.is_ai_generated && <span style={{ fontSize: '10px', background: '#ede9fe', color: '#7c3aed', padding: '2px 6px', borderRadius: '8px', marginLeft: '6px' }}>Community Resource</span>}
                    <span className="public-post-time">{formatTimeAgo(post.created_at)}</span>
                    {post.category && post.category !== 'general' && (
                      <span className="public-post-tag">{post.category}</span>
                    )}
                  </div>
                  {post.title && (
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '6px' }}>{post.title}</div>
                  )}
                  <div className="public-post-content">{post.content}</div>
                  <div className="public-post-actions">
                    <span>❤️ {post.like_count}</span>
                    <button
                      className="public-comment-toggle"
                      onClick={() => post.comment_count > 0 && toggleComments(post.post_id)}
                      disabled={post.comment_count === 0}
                    >
                      💬 {post.comment_count} {post.comment_count === 1 ? 'comment' : 'comments'}
                      {post.comment_count > 0 && <span className="public-comment-arrow">{openComments[post.post_id] ? ' ▲' : ' ▼'}</span>}
                    </button>
                    {!isLoggedInReadonly && <Link to={registerUrl} className="public-post-reply-link">Reply anonymously →</Link>}
                  </div>

                  {openComments[post.post_id] && comments[post.post_id] && (
                    <div className="public-comments-section">
                      {comments[post.post_id].map(c => (
                        <div key={c.comment_id} className="public-comment">
                          <div className="public-comment-header">
                            <span>👤</span>
                            <span className="public-comment-author">Anonymous</span>
                            <span className="public-comment-time">{formatTimeAgo(c.created_at)}</span>
                          </div>
                          <div className="public-comment-content">{c.content}</div>
                        </div>
                      ))}
                      {comments[post.post_id].length === 0 && (
                        <div className="public-comment-empty">No comments yet.</div>
                      )}
                      {!isLoggedInReadonly && (
                        <Link to={registerUrl} className="public-comment-reply-cta">
                          🔒 Sign up to add a comment
                        </Link>
                      )}
                      {isLoggedInReadonly && (
                        <div className="public-comment-reply-cta" style={{ color: '#9ca3af', cursor: 'default' }}>
                          👀 Add this identity to participate
                        </div>
                      )}
                    </div>
                  )}
                </div>
                </React.Fragment>
              ))}

              {hasMore && (
                <button
                  className="public-feed-load-more"
                  onClick={() => fetchChannel(page + 1)}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load more'}
                </button>
              )}
            </div>
          )}

          {/* ── Email Subscribe (anonymous visitors only) ── */}
          {!user && <EmailSubscribeBox channelSlug={slug} categoryName={channel?.category_display} />}

          {isLoggedInReadonly ? (
            <div className="public-channel-cta" style={{ marginTop: '32px', background: '#fefce8', borderColor: '#fde68a' }}>
              <h3>Want to participate in this community?</h3>
              <p>Add this identity to your profile to post, comment, and connect with people here.</p>
              <Link to="/profile" className="public-cta-btn" style={{ background: '#f59e0b' }}>Add Identity to My Profile</Link>
            </div>
          ) : (
            <div className="public-channel-cta" style={{ marginTop: '32px' }}>
              <h3>Join this anonymous community</h3>
              <p>Post, comment, and connect with people who share your experience. You stay anonymous — identity is only revealed through 3 levels of mutual consent.</p>
              <Link to={registerUrl} className="public-cta-btn">Join Belong — It's Free & Anonymous</Link>
            </div>
          )}
        </div>
      )}

      <div className="public-channel-footer">
        <p>💜 Belong — Find where you belong, safely</p>
        <div className="public-footer-links">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/about">About</Link>
        </div>
      </div>

      {!isLoggedInReadonly && (
        <div className="public-sticky-bar">
          <span>🔒 Reading anonymously</span>
          <div className="public-sticky-bar-actions">
            <Link to={loginUrl} className="public-sticky-login">Log In</Link>
            <Link to={registerUrl} className="public-sticky-signup">Sign Up to Reply</Link>
          </div>
        </div>
      )}

      {/* ── AI Companion (anonymous visitors only) ── */}
      {!user && <AiCompanion channelSlug={slug} />}
    </div>
  );
}

export default PublicChannelFeed;
